import React, { useState, useEffect, useRef } from 'react'
import './patterngenerator.css'
import { API_ENDPOINTS } from '../../utils/config'
import { styled } from '@mui/material/styles';
import { Grid, Box, Card, Container, Paper, Fade, Typography, Button, CircularProgress, Alert, Tabs, Tab, ImageList, ImageListItem, Slider, Switch, FormControlLabel, Chip } from '@mui/material'
import FooterSection from '../../Components/Footer/footer'
import NavBar from '../../Components/NavBar/navbar'
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TransformIcon from '@mui/icons-material/Transform';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import PatternIcon from '@mui/icons-material/Pattern';
import PhotoIcon from '@mui/icons-material/Photo';

// Theme
const THEME = {
    bg: '#0a0a0a',
    cardBg: '#18181b',
    border: '#333',
    primary: '#2cbb5d',
    secondary: '#a1a1aa',
    accent: '#ef4743',
    grid: '#222'
};

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

// Styled Components for the new theme
const StyledCard = styled(Card)(({ theme }) => ({
    backgroundColor: THEME.cardBg,
    border: `1px solid ${THEME.border}`,
    borderRadius: '16px',
    padding: '24px',
    height: '100%',
    position: 'relative',
    overflow: 'visible',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-8px)',
        borderColor: THEME.primary,
        boxShadow: `0 10px 30px -10px rgba(44, 187, 93, 0.2)`
    }
}));

const UploadBox = styled(Box)(({ theme, hasFile }) => ({
    border: `2px dashed ${hasFile ? THEME.primary : '#333'}`,
    borderRadius: '12px',
    padding: hasFile ? 0 : '24px',
    textAlign: 'center',
    backgroundColor: hasFile ? 'rgba(44, 187, 93, 0.03)' : 'rgba(255,255,255,0.01)',
    transition: 'all 0.2s ease',
    height: '260px',
    minHeight: '260px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
        borderColor: THEME.primary,
        backgroundColor: 'rgba(44, 187, 93, 0.05)'
    }
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
    background: 'rgba(255, 255, 255, 0.02)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${THEME.border}`,
    borderRadius: '12px',
}));

function PatternGenerator() {

    // State management
    const [file1, setFile1] = useState(null);
    const [file2, setFile2] = useState(null);
    const [singleFile, setSingleFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [transformationFrames, setTransformationFrames] = useState([]);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [modelInfo, setModelInfo] = useState(null);

    // Animation states
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentFrame, setCurrentFrame] = useState(0);
    const [animationSpeed, setAnimationSpeed] = useState(500); // milliseconds between frames
    const [showAsVideo, setShowAsVideo] = useState(true);
    const [loop, setLoop] = useState(true);
    const animationRef = useRef(null);
    const canvasRef = useRef(null);

    // API base URL (dev: :5001 · prod: /svc/pattern via config)
    const API_BASE = API_ENDPOINTS.TURING_PATTERNS;

    // Animation control functions
    const startAnimation = () => {
        if (transformationFrames.length === 0) return;

        setIsPlaying(true);
        animationRef.current = setInterval(() => {
            setCurrentFrame(prevFrame => {
                const nextFrame = (prevFrame + 1) % transformationFrames.length;
                if (!loop && nextFrame === 0 && prevFrame === transformationFrames.length - 1) {
                    setIsPlaying(false);
                    return prevFrame;
                }
                return nextFrame;
            });
        }, animationSpeed);
    };

    const pauseAnimation = () => {
        setIsPlaying(false);
        if (animationRef.current) {
            clearInterval(animationRef.current);
            animationRef.current = null;
        }
    };

    const resetAnimation = () => {
        pauseAnimation();
        setCurrentFrame(0);
    };

    const createGIF = async () => {
        if (transformationFrames.length === 0) return;

        try {
            // Try to use gif.js if available
            const GIF = (await import('gif.js')).default;

            const gif = new GIF({
                workers: 2,
                quality: 10,
                width: 128,
                height: 128,
                repeat: loop ? 0 : -1
            });

            // Convert each frame to canvas and add to GIF
            for (let i = 0; i < transformationFrames.length; i++) {
                const img = new Image();
                img.crossOrigin = 'anonymous';

                await new Promise((resolve) => {
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = 128;
                        canvas.height = 128;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, 128, 128);

                        gif.addFrame(canvas, { delay: animationSpeed });
                        resolve();
                    };
                    img.src = `data:image/png;base64,${transformationFrames[i]}`;
                });
            }

            gif.on('finished', function (blob) {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'transformation.gif';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                setSuccess('GIF saved.');
            });

            gif.render();

        } catch (error) {
            console.error('GIF creation failed:', error);
            // Fallback: Download frames as ZIP or individual images
            downloadAllFrames();
        }
    };

    const downloadAllFrames = () => {
        transformationFrames.forEach((frame, index) => {
            const link = document.createElement('a');
            link.download = `transformation_frame_${index + 1}.png`;
            link.href = `data:image/png;base64,${frame}`;
            link.click();
            // Add small delay between downloads
            setTimeout(() => { }, 100);
        });
        setSuccess('Frames saved.');
    };

    const createVideoBlob = async () => {
        if (transformationFrames.length === 0) return;

        try {
            // Create video using MediaRecorder API
            const canvas = document.createElement('canvas');
            canvas.width = 400;
            canvas.height = 400;
            const ctx = canvas.getContext('2d');

            const stream = canvas.captureStream(30); // 30 FPS
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'video/webm;codecs=vp9'
            });

            const chunks = [];
            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunks, { type: 'video/webm' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'transformation.webm';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                setSuccess('Video saved.');
            };

            mediaRecorder.start();

            // Animate through frames
            let frameIndex = 0;
            const animateVideo = () => {
                if (frameIndex >= transformationFrames.length) {
                    // Loop if enabled, otherwise stop
                    if (loop) {
                        frameIndex = 0;
                    } else {
                        setTimeout(() => mediaRecorder.stop(), 1000);
                        return;
                    }
                }

                const img = new Image();
                img.onload = () => {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    frameIndex++;
                    setTimeout(animateVideo, animationSpeed);
                };
                img.src = `data:image/png;base64,${transformationFrames[frameIndex]}`;
            };

            animateVideo();

            // Stop recording after a reasonable duration
            setTimeout(() => {
                if (mediaRecorder.state === 'recording') {
                    mediaRecorder.stop();
                }
            }, transformationFrames.length * animationSpeed * (loop ? 3 : 1));

        } catch (error) {
            console.error('Video creation failed:', error);
            setError("Couldn't make a video — exporting a GIF instead.");
            createGIF();
        }
    };

    // Cleanup animation on unmount
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                clearInterval(animationRef.current);
            }
        };
    }, []);

    // Auto-start animation when frames are loaded
    useEffect(() => {
        if (transformationFrames.length > 0 && showAsVideo) {
            resetAnimation();
            setTimeout(() => startAnimation(), 500); // Small delay to let UI update
        }
        // Intentionally omit reset/start helpers to avoid restart loops.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transformationFrames, showAsVideo]);

    // Handle speed changes
    useEffect(() => {
        if (isPlaying) {
            pauseAnimation();
            startAnimation();
        }
        // Restart on speed change only; helpers are stable enough for this intent.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animationSpeed]);

    // File upload handlers
    function handleFile1Change(e) {
        const file = e.target.files[0];
        if (file) {
            setFile1({
                file: file,
                preview: URL.createObjectURL(file)
            });
        }
    }

    function handleFile2Change(e) {
        const file = e.target.files[0];
        if (file) {
            setFile2({
                file: file,
                preview: URL.createObjectURL(file)
            });
        }
    }

    function handleSingleFileChange(e) {
        const file = e.target.files[0];
        if (file) {
            setSingleFile({
                file: file,
                preview: URL.createObjectURL(file)
            });
        }
    }

    // API calls
    const checkHealth = async () => {
        try {
            const response = await fetch(`${API_BASE}/health`);
            const data = await response.json();
            console.log('Server health:', data);
            return data;
        } catch (error) {
            console.error('Health check failed:', error);
            setError("Could not reach pattern services.");
            return null;
        }
    };

    const getModelInfo = async () => {
        try {
            const response = await fetch(`${API_BASE}/model-info`);
            const data = await response.json();
            setModelInfo(data);
        } catch (error) {
            console.error('Failed to get model info:', error);
        }
    };

    const transformImages = async () => {
        if (!file1 || !file2) {
            setError('Upload both images first.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);
        setTransformationFrames([]);

        try {
            // Check server health first
            const health = await checkHealth();
            if (!health) return;

            const formData = new FormData();
            formData.append('image1', file1.file);
            formData.append('image2', file2.file);

            const response = await fetch(`${API_BASE}/transform`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                setTransformationFrames(data.frames);
                setSuccess('Transformation done.');
            } else {
                console.error('Transform failed:', data.error || data);
                setError("Couldn't transform — try again.");
            }
        } catch (error) {
            console.error('Transform error:', error);
            setError("Couldn't transform — check your connection.");
        } finally {
            setLoading(false);
        }
    };

    const generateImage = async () => {
        if (!singleFile) {
            setError('Upload a seed image first.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);
        setGeneratedImage(null);

        try {
            // Check server health first
            const health = await checkHealth();
            if (!health) return;

            const formData = new FormData();
            formData.append('image', singleFile.file);

            const response = await fetch(`${API_BASE}/generate`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                setGeneratedImage(data.generated_image);
                setSuccess('Pattern ready.');
            } else {
                console.error('Generate failed:', data.error || data);
                setError("Couldn't generate — try again.");
            }
        } catch (error) {
            console.error('Generate error:', error);
            setError("Couldn't generate — check your connection.");
        } finally {
            setLoading(false);
        }
    };

    // Component initialization
    React.useEffect(() => {
        getModelInfo();
        checkHealth();
        // Mount-only health/model probe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        // Clear previous results when switching tabs
        setError(null);
        setSuccess(null);
        setTransformationFrames([]);
        setGeneratedImage(null);
    };

    return (
        <Box sx={{
            bgcolor: THEME.bg,
            minHeight: '100vh',
            backgroundImage: `
                linear-gradient(180deg, #0a0a0a 0%, #111 100%), 
                linear-gradient(#333 1px, transparent 1px), 
                linear-gradient(90deg, #333 1px, transparent 1px)
            `,
            backgroundSize: '100% 100%, 40px 40px, 40px 40px',
            backgroundPosition: '0 0, 0 0, 0 0',
            backgroundBlendMode: 'overlay',
        }}>
            <NavBar />
            <Box sx={{ pt: 12, pb: 8 }}>
                <Container maxWidth="xl">
                    {/* Header Section */}
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Fade in={true} timeout={800}>
                            <Box>
                                <Box sx={{
                                    display: 'inline-flex',
                                    p: 2.5,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(44, 187, 93, 0.1)',
                                    mb: 3,
                                    boxShadow: `0 0 40px rgba(44, 187, 93, 0.2)`
                                }}>
                                    <PatternIcon sx={{ fontSize: 48, color: THEME.primary }} />
                                </Box>
                                <Typography variant="h2" sx={{
                                    color: 'white',
                                    fontWeight: 800,
                                    mb: 2,
                                    letterSpacing: '-1px'
                                }}>
                                    AI Turing <span style={{
                                        background: `linear-gradient(90deg, ${THEME.primary} 0%, #1e8e45 100%)`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent'
                                    }}>Patterns</span>
                                </Typography>
                                <Typography variant="h6" sx={{ color: THEME.secondary, maxWidth: '700px', mx: 'auto', lineHeight: 1.6, mb: 4 }}>
                                    Turing patterns from reaction–diffusion. Upload images and watch them morph.
                                    Upload images to explore activator-inhibitor pattern formation.
                                </Typography>
                            </Box>
                        </Fade>

                        {modelInfo && (
                            <Chip
                                label={`TuringNet ${modelInfo.models?.turing_net ? 'Online' : 'Offline'} • Direct ${modelInfo.models?.turing_net_direct ? 'Online' : 'Offline'}`}
                                color={modelInfo.models?.turing_net ? "success" : "default"}
                                variant="outlined"
                                sx={{
                                    borderColor: modelInfo.models?.turing_net ? THEME.primary : THEME.border,
                                    color: modelInfo.models?.turing_net ? THEME.primary : THEME.secondary,
                                    fontWeight: 600,
                                    bgcolor: 'rgba(0,0,0,0.4)'
                                }}
                            />
                        )}
                    </Box>

                    {/* Tabs for different modes */}
                    <GlassPaper sx={{
                        mb: 6,
                        mx: 'auto',
                        maxWidth: '600px',
                        overflow: 'hidden'
                    }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            centered
                            sx={{
                                '& .MuiTab-root': {
                                    color: THEME.secondary,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    px: 4,
                                    py: 2,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.05)'
                                    },
                                    '&.Mui-selected': {
                                        color: THEME.primary,
                                        fontWeight: 700
                                    }
                                },
                                '& .MuiTabs-indicator': {
                                    bgcolor: THEME.primary,
                                    height: '3px',
                                }
                            }}
                        >
                            <Tab label="Transform" icon={<TransformIcon />} iconPosition="start" />
                            <Tab label="Generate Pattern" icon={<AutoAwesomeIcon />} iconPosition="start" />
                        </Tabs>
                    </GlassPaper>

                    {/* Messages */}
                    {error && (
                        <Fade in={true} timeout={300}>
                            <Alert
                                severity="error"
                                variant="filled"
                                sx={{
                                    mb: 4,
                                    maxWidth: '800px',
                                    mx: 'auto',
                                    bgcolor: 'rgba(239, 71, 67, 0.15)',
                                    color: '#ff8a80',
                                    border: `1px solid ${THEME.accent}`,
                                    backdropFilter: 'blur(5px)'
                                }}
                            >
                                {error}
                            </Alert>
                        </Fade>
                    )}
                    {success && (
                        <Fade in={true} timeout={300}>
                            <Alert
                                severity="success"
                                variant="filled"
                                sx={{
                                    mb: 4,
                                    maxWidth: '800px',
                                    mx: 'auto',
                                    bgcolor: 'rgba(44, 187, 93, 0.15)',
                                    color: '#b9f6ca',
                                    border: `1px solid ${THEME.primary}`,
                                    backdropFilter: 'blur(5px)'
                                }}
                            >
                                {success}
                            </Alert>
                        </Fade>
                    )}

                    <Grid container spacing={4}>

                        {/* Tab Content */}
                        {tabValue === 0 && (
                            <>
                                {/* Image Transformation Mode */}
                                <Grid item xs={12} md={6}>
                                    <StyledCard>
                                        <Box sx={{ borderBottom: `1px solid ${THEME.border}`, pb: 2, mb: 3 }}>
                                            <Typography variant='h6' fontWeight={700} color="white">
                                                Source Image
                                            </Typography>
                                            <Typography variant='body2' color={THEME.secondary}>
                                                The starting point of the transformation
                                            </Typography>
                                        </Box>

                                        <UploadBox
                                            component="label"
                                            hasFile={!!file1}
                                        >
                                            {file1 ? (
                                                <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                                                    <img
                                                        src={file1.preview}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                        alt="Source"
                                                    />
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s',
                                                        bgcolor: 'rgba(0,0,0,0.6)',
                                                        '&:hover': { opacity: 1 }
                                                    }}>
                                                        <Typography variant="button" sx={{ color: 'white', fontWeight: 'bold' }}>Change Image</Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <>
                                                    <CloudUploadIcon sx={{ fontSize: 48, color: THEME.secondary, mb: 2 }} />
                                                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                                                        Upload image
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: THEME.secondary, mt: 1 }}>
                                                        SVG, PNG, JPG or GIF (max. 5MB)
                                                    </Typography>
                                                </>
                                            )}
                                            <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFile1Change} />
                                        </UploadBox>
                                    </StyledCard>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <StyledCard>
                                        <Box sx={{ borderBottom: `1px solid ${THEME.border}`, pb: 2, mb: 3 }}>
                                            <Typography variant='h6' fontWeight={700} color="white">
                                                Target Image
                                            </Typography>
                                            <Typography variant='body2' color={THEME.secondary}>
                                                The final destination of the pattern
                                            </Typography>
                                        </Box>

                                        <UploadBox
                                            component="label"
                                            hasFile={!!file2}
                                        >
                                            {file2 ? (
                                                <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                                                    <img
                                                        src={file2.preview}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                        alt="Target"
                                                    />
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s',
                                                        bgcolor: 'rgba(0,0,0,0.6)',
                                                        '&:hover': { opacity: 1 }
                                                    }}>
                                                        <Typography variant="button" sx={{ color: 'white', fontWeight: 'bold' }}>Change Image</Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <>
                                                    <PhotoIcon sx={{ fontSize: 48, color: THEME.secondary, mb: 2 }} />
                                                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                                                        Upload image
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: THEME.secondary, mt: 1 }}>
                                                        SVG, PNG, JPG or GIF (max. 5MB)
                                                    </Typography>
                                                </>
                                            )}
                                            <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFile2Change} />
                                        </UploadBox>
                                    </StyledCard>
                                </Grid>

                                {/* Transform Button */}
                                <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', py: 2 }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={transformImages}
                                            disabled={loading || !file1 || !file2}
                                            sx={{
                                                px: 6,
                                                py: 2,
                                                fontSize: '1.1rem',
                                                fontWeight: 800,
                                                borderRadius: '50px',
                                                bgcolor: THEME.primary,
                                                color: 'black',
                                                boxShadow: `0 0 20px rgba(44, 187, 93, 0.4)`,
                                                '&:hover': {
                                                    bgcolor: '#249a4d',
                                                    transform: 'scale(1.05)',
                                                    boxShadow: `0 0 30px rgba(44, 187, 93, 0.6)`
                                                },
                                                '&:disabled': {
                                                    bgcolor: '#333',
                                                    color: '#666'
                                                }
                                            }}
                                        >
                                            {loading ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <CircularProgress size={24} color="inherit" />
                                                    <span>Processing...</span>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PlayArrowIcon />
                                                    <span>Transform</span>
                                                </Box>
                                            )}
                                        </Button>
                                    </Box>
                                </Grid>

                                {/* Transformation Results */}
                                {transformationFrames.length > 0 && (
                                    <Grid item xs={12}>
                                        <StyledCard sx={{ border: `1px solid ${THEME.primary}`, boxShadow: `0 0 40px -10px rgba(44, 187, 93, 0.15)` }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                                                <Box>
                                                    <Typography variant='h5' fontWeight={800} color="white">
                                                        Transformation Result
                                                    </Typography>
                                                    <Typography variant='body2' color={THEME.secondary}>
                                                        AI-generated morphing sequence
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', bgcolor: 'rgba(0,0,0,0.3)', p: 1, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={showAsVideo}
                                                                onChange={(e) => {
                                                                    setShowAsVideo(e.target.checked);
                                                                    if (!e.target.checked) pauseAnimation();
                                                                }}
                                                                size="small"
                                                                color="success"
                                                            />
                                                        }
                                                        label={<Typography variant="body2" color="white">Animation</Typography>}
                                                        sx={{ mr: 1, ml: 1 }}
                                                    />
                                                    <Button
                                                        variant="text"
                                                        startIcon={<DownloadIcon />}
                                                        onClick={createGIF}
                                                        size="small"
                                                        sx={{ color: THEME.primary }}
                                                    >
                                                        GIF
                                                    </Button>
                                                    <Button
                                                        variant="text"
                                                        startIcon={<DownloadIcon />}
                                                        onClick={createVideoBlob}
                                                        size="small"
                                                        sx={{ color: 'white' }}
                                                    >
                                                        Video
                                                    </Button>
                                                </Box>
                                            </Box>

                                            {showAsVideo ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                                                    {/* Screen Container */}
                                                    <Box sx={{
                                                        p: 1,
                                                        borderRadius: '16px',
                                                        background: 'linear-gradient(145deg, #222, #111)',
                                                        border: '4px solid #333',
                                                        boxShadow: '0 20px 50px rgba(0,0,0,0.5), inset 0 0 20px rgba(0,0,0,0.8)'
                                                    }}>
                                                        <Box
                                                            sx={{
                                                                // Match UploadBox panel height so result display aligns with inputs
                                                                position: 'relative',
                                                                width: '100%',
                                                                maxWidth: '600px',
                                                                height: '260px',
                                                                bgcolor: 'black',
                                                                borderRadius: '8px',
                                                                overflow: 'hidden',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            <img
                                                                ref={canvasRef}
                                                                src={`data:image/png;base64,${transformationFrames[currentFrame]}`}
                                                                alt={`Frame ${currentFrame + 1}`}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                    display: 'block',
                                                                }}
                                                            />
                                                            {/* CRT Scanline Effect */}
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                background: 'linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06))',
                                                                backgroundSize: '100% 2px, 3px 100%',
                                                                pointerEvents: 'none',
                                                                zIndex: 10
                                                            }} />
                                                        </Box>
                                                    </Box>

                                                    {/* Controls Container */}
                                                    <GlassPaper sx={{
                                                        p: 3,
                                                        width: '100%',
                                                        maxWidth: '700px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 2
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Button
                                                                variant="contained"
                                                                onClick={isPlaying ? pauseAnimation : startAnimation}
                                                                sx={{ minWidth: '48px', width: '48px', height: '48px', borderRadius: '50%', bgcolor: 'white', color: 'black', '&:hover': { bgcolor: '#ddd' } }}
                                                            >
                                                                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                                                            </Button>

                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Slider
                                                                    value={currentFrame}
                                                                    onChange={(e, value) => {
                                                                        pauseAnimation();
                                                                        setCurrentFrame(value);
                                                                    }}
                                                                    min={0}
                                                                    max={transformationFrames.length - 1}
                                                                    step={1}
                                                                    sx={{
                                                                        color: THEME.primary,
                                                                        height: 6,
                                                                        '& .MuiSlider-thumb': {
                                                                            width: 16, height: 16,
                                                                            border: '2px solid white',
                                                                        }
                                                                    }}
                                                                />
                                                            </Box>

                                                            <Typography variant="caption" sx={{ color: 'white', fontFamily: 'monospace', minWidth: '60px', textAlign: 'right' }}>
                                                                {currentFrame + 1}/{transformationFrames.length}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                <Typography variant="caption" sx={{ color: THEME.secondary }}>Speed</Typography>
                                                                <Slider
                                                                    value={animationSpeed}
                                                                    onChange={(e, value) => setAnimationSpeed(value)}
                                                                    min={100}
                                                                    max={2000}
                                                                    step={100}
                                                                    size="small"
                                                                    sx={{ width: 100, color: THEME.secondary }}
                                                                />
                                                            </Box>

                                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                                <FormControlLabel
                                                                    control={<Switch checked={loop} onChange={(e) => setLoop(e.target.checked)} size="small" color="success" />}
                                                                    label={<Typography variant="caption" color={THEME.secondary}>Loop</Typography>}
                                                                />
                                                                <Button startIcon={<RestartAltIcon />} onClick={resetAnimation} size="small" sx={{ color: 'white' }}>
                                                                    Reset
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </GlassPaper>
                                                </Box>
                                            ) : (
                                                /* Grid View */
                                                <Box sx={{
                                                    p: 2,
                                                    background: '#111',
                                                    borderRadius: '12px',
                                                    border: '1px solid #333'
                                                }}>
                                                    <ImageList
                                                        sx={{
                                                            width: '100%',
                                                            maxHeight: 500,
                                                            '&::-webkit-scrollbar': { width: '8px' },
                                                            '&::-webkit-scrollbar-thumb': { background: '#333', borderRadius: '4px' }
                                                        }}
                                                        cols={6}
                                                        gap={8}
                                                    >
                                                        {transformationFrames.map((frame, index) => (
                                                            <ImageListItem
                                                                key={index}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    borderRadius: '8px',
                                                                    overflow: 'hidden',
                                                                    border: currentFrame === index ? `2px solid ${THEME.primary}` : '1px solid #333',
                                                                    transition: 'all 0.2s',
                                                                    '&:hover': { opacity: 0.8, transform: 'scale(1.05)', zIndex: 2 }
                                                                }}
                                                                onClick={() => {
                                                                    setCurrentFrame(index);
                                                                    setShowAsVideo(true);
                                                                }}
                                                            >
                                                                <img
                                                                    src={`data:image/png;base64,${frame}`}
                                                                    alt={`Frame ${index + 1}`}
                                                                    style={{ objectFit: 'cover', aspectRatio: '1/1' }}
                                                                />
                                                            </ImageListItem>
                                                        ))}
                                                    </ImageList>
                                                </Box>
                                            )}
                                        </StyledCard>
                                    </Grid>
                                )}
                            </>
                        )}

                        {tabValue === 1 && (
                            <>
                                {/* Pattern Generation Mode */}
                                <Grid item xs={12} md={6}>
                                    <StyledCard>
                                        <Box sx={{ borderBottom: `1px solid ${THEME.border}`, pb: 2, mb: 3 }}>
                                            <Typography variant='h6' fontWeight={700} color="white">
                                                Seed Image
                                            </Typography>
                                            <Typography variant='body2' color={THEME.secondary}>
                                                Upload an image to base the pattern on
                                            </Typography>
                                        </Box>

                                        <UploadBox
                                            component="label"
                                            hasFile={!!singleFile}
                                        >
                                            {singleFile ? (
                                                <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                                                    <img
                                                        src={singleFile.preview}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                        alt="Input"
                                                    />
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s',
                                                        bgcolor: 'rgba(0,0,0,0.6)',
                                                        '&:hover': { opacity: 1 }
                                                    }}>
                                                        <Typography variant="button" sx={{ color: 'white', fontWeight: 'bold' }}>Change Image</Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <>
                                                    <AutoAwesomeIcon sx={{ fontSize: 48, color: THEME.secondary, mb: 2 }} />
                                                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                                                        Upload Seed
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: THEME.secondary, mt: 1 }}>
                                                        Use high contrast images for best results
                                                    </Typography>
                                                </>
                                            )}
                                            <VisuallyHiddenInput type="file" accept="image/*" onChange={handleSingleFileChange} />
                                        </UploadBox>

                                        <Box sx={{ textAlign: 'center', mt: 4 }}>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                onClick={generateImage}
                                                disabled={loading || !singleFile}
                                                fullWidth
                                                sx={{
                                                    py: 1.5,
                                                    bgcolor: THEME.primary,
                                                    color: 'black',
                                                    fontWeight: 'bold',
                                                    '&:hover': { bgcolor: '#249a4d' }
                                                }}
                                            >
                                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Generate Pattern'}
                                            </Button>
                                        </Box>
                                    </StyledCard>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <StyledCard>
                                        <Box sx={{ borderBottom: `1px solid ${THEME.border}`, pb: 2, mb: 3 }}>
                                            <Typography variant='h6' fontWeight={700} color="white">
                                                Generated Output
                                            </Typography>
                                            <Typography variant='body2' color={THEME.secondary}>
                                                The AI hallucinated pattern
                                            </Typography>
                                        </Box>

                                        {generatedImage ? (
                                            <Box sx={{
                                                // Same panel size as seed UploadBox for consistent presentation
                                                width: '100%',
                                                height: '260px',
                                                bgcolor: 'black',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
                                            }}>
                                                <img
                                                    src={`data:image/png;base64,${generatedImage}`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                    alt="Generated"
                                                />
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: '260px',
                                                    border: '2px dashed #333',
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'text.secondary',
                                                    bgcolor: 'rgba(0,0,0,0.2)'
                                                }}
                                            >
                                                <PatternIcon sx={{ fontSize: 60, opacity: 0.1, mb: 2 }} />
                                                <Typography variant="body2" color={THEME.secondary}>
                                                    Waiting for input...
                                                </Typography>
                                            </Box>
                                        )}
                                    </StyledCard>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Container>
            </Box>
            <FooterSection />
        </Box>
    )
}
export default PatternGenerator;